import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Eye, Truck } from 'lucide-react';

import { AttentionReasons } from '../../../../components/deliveries/AttentionReasons';
import { DataTable } from '../../../../components/ui/DataTable';
import { DropdownMenu } from '../../../../components/ui/DropdownMenu';
import { CopyableText } from '../../../../components/ui/CopyableText';
import { DateRangePicker } from '../../../../components/ui/DateRangePicker';
import { STATUS_MAP } from '../../../../utils/catalogs';
import { getChartColor } from '../../../../utils/chartColors';
import { formatRelativeTime } from '../../../../features/dashboard/dashboardUtils';
import { fetchAttention } from '../../../../features/attention/attentionThunks';
import { setFilters } from '../../../../features/attention/attentionSlice';
import {
  ATTENTION_REASON,
  ATTENTION_REASONS,
  ATTENTION_REASON_MAP,
} from '../../../../features/attention/attentionConstants';
import { COURIER_CHANGE_STATUSES } from '../../../../features/attention/attentionActionThunks';
import { AttentionActions } from './components/AttentionActions';
import { ChangeCourierModal } from './modals/ChangeCourierModal';
import { selectHasFulfillmentPro } from '../../../../features/auth/authSelectors';

/**
 * The seller's attention queue: the dashboard card's predicates over its own
 * shipments. `api/deliveries/attention/` scopes itself from the session.
 *
 * Changing the courier is the only action. Changing statuses is
 * `IsStaffAdminUser` and requesting tracking numbers is staff-only, so the
 * backend would answer a seller with an error on both.
 */
export const DeliveriesAttention = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data, total, isLoading, error, filters } = useSelector((state) => state.attention);
  const hasFulfillmentPro = useSelector(selectHasFulfillmentPro);

  // Arriving from a dashboard card carries its reason in the URL, so the link
  // is shareable and a reload does not drop you back into "Todos".
  const reasonFromUrl = searchParams.get('reason');
  useEffect(() => {
    if (reasonFromUrl && reasonFromUrl !== filters.reason) {
      dispatch(setFilters({ reason: reasonFromUrl }));
    }
    // Only on arrival: after that the chips own the reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasonFromUrl]);

  useEffect(() => {
    dispatch(fetchAttention());
  }, [dispatch, filters]);

  const handleFilterChange = useCallback(
    (patch) => dispatch(setFilters(patch)),
    [dispatch]
  );

  const [selectedRows, setSelectedRows] = useState([]);
  /** The rows the courier modal acts on; `null` while it is closed. */
  const [courierRows, setCourierRows] = useState(null);

  const rowsById = useMemo(() => {
    const map = {};
    data.forEach((row) => { map[row.delivery_id] = row; });
    return map;
  }, [data]);

  const closeCourierModal = useCallback(() => setCourierRows(null), []);

  const afterAction = useCallback(() => {
    dispatch(fetchAttention());
    setSelectedRows([]);
  }, [dispatch]);

  const buildRowActions = (row) => {
    const canChangeCourier = COURIER_CHANGE_STATUSES.includes(row.status);

    return [
      {
        label: 'Ver detalle de la orden',
        icon: Eye,
        onClick: () => navigate(`/order-details/${row.order_id}`),
      },
      // Behind the addon like the bulk one: for two stuck shipments, one by one
      // is the same thing.
      ...(hasFulfillmentPro
        ? [{
          label: 'Cambiar courier',
          icon: Truck,
          onClick: () => setCourierRows([row]),
          disabled: !canChangeCourier,
          description: canChangeCourier
            ? undefined
            : 'Solo se puede antes de que el envío salga al courier',
        }]
        : []),
    ];
  };

  const current = ATTENTION_REASON_MAP[filters.reason] || ATTENTION_REASON_MAP[ATTENTION_REASON.ALL];

  const columns = [
    {
      header: 'Orden',
      accessor: 'order_id',
      cell: (row) => (
        <div className="flex flex-col">
          <Link
            to={`/order-details/${row.order_id}`}
            title="Ver Detalles"
            data-testid="attention-order-link"
            className="font-bold text-ninja-dark dark:text-white hover:text-ninja-green hover:underline transition-colors w-fit"
          >
            #{row.order_id}
          </Link>
          {row.reference_id && (
            <span className="text-xs text-text-muted">{row.reference_id}</span>
          )}
        </div>
      ),
    },
    {
      header: 'Estado',
      accessor: 'status',
      cell: (row) => {
        const color = getChartColor(row.status);
        return (
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{ backgroundColor: `${color}1a`, color }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
            {STATUS_MAP[row.status]?.user_name || row.status}
          </span>
        );
      },
    },
    {
      header: 'Motivo',
      accessor: 'reason',
      cell: (row) => <AttentionReasons row={row} />,
    },
    {
      header: 'Último movimiento',
      accessor: 'last_movement',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-text-secondary">
            {row.last_movement ? formatRelativeTime(row.last_movement) : 'Sin historial'}
          </span>
          {!row.last_movement && row.unchanged_since && (
            <span className="text-xs text-text-muted">
              Creado {formatRelativeTime(row.unchanged_since).toLowerCase()}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Destino',
      accessor: 'commune',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-ninja-dark dark:text-white">{row.customer || '-'}</span>
          <span className="text-xs text-text-muted">{row.commune || 'Sin comuna'}</span>
        </div>
      ),
    },
    {
      header: 'Tracking',
      accessor: 'tracking_number',
      cell: (row) =>
        row.tracking_number
          ? <CopyableText value={row.tracking_number} maxWidth="140px" />
          : <span className="text-xs text-text-muted">Sin número</span>,
    },
    {
      header: '',
      cell: (row) => <DropdownMenu items={buildRowActions(row)} />,
    },
  ];

  return (
    <div data-testid="attention-page" className="space-y-6">
      <div className="flex flex-col gap-3">
        <div data-testid="attention-reasons" className="flex flex-wrap items-center gap-2.5">
          {ATTENTION_REASONS.map((reason) => {
            const isActive = filters.reason === reason.value;
            return (
              <button
                key={reason.value}
                data-testid="attention-reason"
                data-reason={reason.value}
                onClick={() => handleFilterChange({ reason: reason.value })}
                className={clsx(
                  'px-4 py-2 rounded-full text-sm font-semibold border transition-all',
                  isActive
                    ? 'bg-ninja-green text-white border-ninja-green shadow-sm'
                    : 'bg-white dark:bg-[#1a1a1a] text-text-secondary border-border hover:bg-ninja-gray dark:hover:bg-[#2d2d2d]'
                )}
              >
                {reason.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <AlertTriangle size={15} className="text-warning flex-shrink-0" />
          {current.description}
        </div>
      </div>

      <div data-testid="attention-filters" className="flex flex-col md:flex-row flex-wrap items-center gap-4">
        <DateRangePicker
          startDate={filters.start_date}
          endDate={filters.end_date}
          onChange={handleFilterChange}
        />
      </div>

      {/* Changing the courier is the only thing this screen does in bulk, and
          it is behind Fulfillment Pro. Without it the bar goes, and so do the
          checkboxes below: a selection that leads nowhere is worse than none. */}
      {hasFulfillmentPro && (
        <AttentionActions
          selectedCount={selectedRows.length}
          onChangeCourier={() => setCourierRows(selectedRows.map((id) => rowsById[id]).filter(Boolean))}
        />
      )}

      <DataTable
        columns={columns}
        data={data}
        totalItems={total}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={isLoading}
        onPageChange={(page) => handleFilterChange({ page })}
        onRowsPerPageChange={(rowsPerPage) => handleFilterChange({ rowsPerPage })}
        idAccessor="delivery_id"
        itemLabel="envíos"
        error={error}
        onRetry={() => dispatch(fetchAttention())}
        emptyMessage="Nada que atender en este periodo. Todo se está moviendo."
        selectedRows={hasFulfillmentPro ? selectedRows : undefined}
        onSelectionChange={hasFulfillmentPro ? setSelectedRows : undefined}
      />

      <ChangeCourierModal
        isOpen={courierRows !== null}
        onClose={closeCourierModal}
        rows={courierRows || []}
        onSuccess={afterAction}
      />
    </div>
  );
};
